import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ItemStatus } from '@features/shopautomation/types.ts';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import BaseSelector from '@/components/selectors/BaseSelector.tsx';
import { equalsIgnoreCase } from '@core/utils';
import { ConfirmationDialogWithIcon } from '@/components';
import { ConfirmationDialogRef } from '@/components/dialog/ConfirmationDialog.tsx';
import { useSettingsStore, useStatusDialogStore } from '@core/store';
import { HEALTH_STAUS } from '@core/utils/statusUtils.ts';
import { operationModeService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { ShopAutomation } from '@core/types';
import { formatLastActivity } from '@core/utils/formatUtils';

interface HealthStatusMonitoringTableProps {
    shopAutomation: ShopAutomation;
    itemStatus: ItemStatus[];
}

const REBOOT = 'reboot';

const HealthStatusMonitoringTable: React.FC<HealthStatusMonitoringTableProps> = ({ shopAutomation, itemStatus }) => {
    const { t } = useTranslation();
    const getRowKey = (item: ItemStatus) => item.id || item.name;

    const [selectedCommands, setSelectedCommands] = useState<Record<string, string | null>>({});
    const [inProgress, setInProgress] = useState<Record<string, boolean>>({});
    const [pendingItem, setPendingItem] = useState<ItemStatus | null>(null);
    const { isMobile } = useSettingsStore();
    const { showStatusLoading, showStatusSuccess, showStatusError } = useStatusDialogStore();
    const { notifyError, notifySuccess } = useNotifications();

    const confirmDialogRef = useRef<ConfirmationDialogRef>(null);

    const getHealthStateIcon = (healthStatusType: string) => {
        if (!healthStatusType) return null;

        if (healthStatusType === HEALTH_STAUS.Healthy || healthStatusType === HEALTH_STAUS.Check)
            return <i className="bi bi-check-circle-fill status-healthy" style={{ fontSize: '16px' }} />;

        if (healthStatusType === HEALTH_STAUS.Unhealthy)
            return <i className="bi bi-x-circle-fill status-error" style={{ fontSize: '16px' }} />;

        return <i className="bi bi-slash-circle-fill status-unknown" style={{ fontSize: '16px' }} />;
    };

    const sendDeviceCommand = useCallback(
        async (
            gatewayId: string,
            moduleId: string,
            deviceType: string,
            deviceId: string,
            selectedCommand: string
        ): Promise<void> => {
            const [command, value] = selectedCommand.split(':');
            const isReboot = equalsIgnoreCase(command, REBOOT);
            try {
                if (isReboot) {
                    showStatusLoading({
                        heading: t('commandDevice', { command }),
                        message: t('sendingCommand'),
                        closable: false
                    });
                }

                await operationModeService.sendDeviceCommand(gatewayId, moduleId, deviceType, deviceId, command, value);

                if (isReboot) {
                    showStatusSuccess({
                        heading: t('commandRequested', { command }),
                        message: t('commandSuccessMessage', { command, device: deviceId })
                    });
                } else {
                    notifySuccess(command, 'successfullySent');
                }
            } catch (error: any) {
                console.error('[HealthStatusMonitoring] Error sending WEAC command:', error);
                if (isReboot) {
                    showStatusError({
                        heading: t('commandFailed', { command }),
                        message: t('commandFailedMessage', { command, device: deviceId }),
                        closable: true,
                        actions: {
                            confirmText: 'tryAgain',
                            onConfirm: () =>
                                sendDeviceCommand(gatewayId, moduleId, deviceType, deviceId, selectedCommand)
                        }
                    });
                } else {
                    notifyError(command, 'failedToSend');
                }
            }
        },
        [t]
    );

    const sendCommand = useCallback(
        async (item: ItemStatus | null): Promise<void> => {
            if (!item) return;

            const key = getRowKey(item);
            const selected = selectedCommands[key];
            if (!selected || !shopAutomation.gateway?.deviceId || !shopAutomation.moduleId) return;

            setInProgress((prev) => ({ ...prev, [key]: true }));
            try {
                await sendDeviceCommand(
                    shopAutomation.gateway?.deviceId,
                    shopAutomation.moduleId,
                    item.deviceOrServiceType || '',
                    item.name || '',
                    selected
                );
            } catch (error) {
                console.error('[HealthStatusMonitoringTable] Error sending command:', error);
            } finally {
                setInProgress((prev) => ({ ...prev, [key]: false }));
            }
        },
        [selectedCommands, shopAutomation?.gateway?.deviceId, shopAutomation?.moduleId, sendDeviceCommand]
    );

    const onSendCommand = useCallback(
        (item: ItemStatus) => {
            if (equalsIgnoreCase(selectedCommands[getRowKey(item)] ?? null, REBOOT)) {
                setPendingItem(item);
                confirmDialogRef.current?.openDialog();
                return;
            }
            void sendCommand(item);
        },
        [selectedCommands, sendCommand]
    );

    const isGateway = (item: ItemStatus) => equalsIgnoreCase(item.deviceOrServiceType, 'gateway');

    const customSort = useCallback((rows: ItemStatus[], sortBy: string, direction: 'asc' | 'desc') => {
        const dir = direction === 'desc' ? -1 : 1;
        return [...rows].sort((a, b) => {
            const groupDiff = Number(!isGateway(a)) - Number(!isGateway(b));
            if (groupDiff !== 0) return groupDiff;

            if (!sortBy) return 0;
            const av = a[sortBy as keyof ItemStatus] ?? '';
            const bv = b[sortBy as keyof ItemStatus] ?? '';
            if (av === bv) return 0;
            return (av < bv ? -1 : 1) * dir;
        });
    }, []);

    const columns: DataTableColumn<ItemStatus>[] = useMemo(
        () => [
            {
                text: t('deviceConnection'),
                value: 'healthStatusType',
                width: 140,
                render: (row) => (
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {!isMobile && (
                            <Box
                                component="span"
                                sx={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}
                            >
                                {equalsIgnoreCase(row.deviceOrServiceType, 'gateway') && (
                                    <i
                                        className="bi bi-pin-angle"
                                        style={{ fontSize: '16px', color: 'var(--gray-medium)' }}
                                    />
                                )}
                            </Box>
                        )}
                        {getHealthStateIcon(row.healthStatusType)}
                    </Box>
                )
            },
            {
                text: t('device'),
                value: 'name',
                isTitle: true,
                width: 200,
                render: (row) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: isMobile ? 'var(--font-family-medium)' : '',
                            overflowX: 'auto'
                        }}
                    >
                        {isMobile && (
                            <Box>
                                {equalsIgnoreCase(row.deviceOrServiceType, 'gateway') && (
                                    <i className="bi bi-pin-angle" style={{ fontSize: '16px', paddingRight: '16px' }} />
                                )}
                            </Box>
                        )}
                        {row.name}
                    </Box>
                )
            },
            {
                text: t('lastActivity'),
                value: 'lastActivityDateTime',
                width: 200,
                render: (row) => formatLastActivity(row.lastActivityDateTime)
            },
            {
                text: '',
                value: '',
                width: 200,
                sortable: false,
                isHidden: (row) => !row.commands?.length,
                render: (row) =>
                    row.commands?.length ? (
                        <BaseSelector
                            value={selectedCommands[getRowKey(row)] ?? null}
                            items={[...new Set(row.commands)].map((c) => ({ text: c, value: c }))}
                            itemText="text"
                            itemValue="value"
                            placeholder={t('selectACommand')}
                            onChange={(val) => setSelectedCommands((prev) => ({ ...prev, [getRowKey(row)]: val }))}
                        />
                    ) : null
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                isAction: true,
                sortable: false,
                render: (row) => {
                    const key = getRowKey(row);
                    return row.commands?.length ? (
                        <IconButton
                            size="small"
                            disabled={
                                !selectedCommands[key] || !shopAutomation.gateway?.deviceId || !shopAutomation.moduleId
                            }
                            className={inProgress[key] ? 'selected' : ''}
                            onClick={() => onSendCommand(row)}
                        >
                            <i className="bi bi-play-circle" />
                        </IconButton>
                    ) : null;
                }
            }
        ],
        [t, selectedCommands, inProgress, shopAutomation?.gateway?.deviceId, shopAutomation?.moduleId, isMobile]
    );

    const rows = useMemo(
        () => (itemStatus ?? []).filter((i) => i?.name).sort((a, b) => Number(!isGateway(a)) - Number(!isGateway(b))),
        [itemStatus]
    );
    const pendingCommand = pendingItem ? (selectedCommands[getRowKey(pendingItem)] ?? '') : '';

    return (
        <>
            <NewWcnTable
                columns={columns}
                data={rows}
                noDataMessage={t('noData')}
                size="small"
                customSort={customSort}
            />

            <ConfirmationDialogWithIcon
                dialogRef={confirmDialogRef}
                heading={`${t('commandDevice', { command: pendingCommand })}?`}
                message={t('commandConfirmMessage', { command: pendingCommand, device: pendingItem?.name })}
                confirmText="yesReboot"
                cancelText="noCancel"
                icon={<i className="bi bi-bootstrap-reboot" />}
                onConfirm={() => sendCommand(pendingItem)}
            />
        </>
    );
};

export default HealthStatusMonitoringTable;
